import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Trash2, DollarSign,  FileText, ChevronRight, Building2, User, Shield } from 'lucide-react';
import { invoiceAPI, clientsAPI, jobsAPI } from "../../services/api";
import { toast } from "react-toastify";

const AddressGrid = ({ addressKey, required = false, formData, handleAddressChange }) => (
  <div className="grid grid-cols-2 gap-4">
    <div className="col-span-2">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Street Address {required && <span className="text-red-500">*</span>}
      </label>
      <input type="text" placeholder="e.g., 123 Business Park"
        value={formData[addressKey].streetLine1}
        onChange={e => handleAddressChange(addressKey, 'streetLine1', e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
        required={required} />
    </div>
    <div className="col-span-2">
      <label className="block text-sm font-medium text-gray-700 mb-1">Street Line 2</label>
      <input type="text" placeholder="Suite, Building, etc."
        value={formData[addressKey].streetLine2}
        onChange={e => handleAddressChange(addressKey, 'streetLine2', e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        City {required && <span className="text-red-500">*</span>}
      </label>
      <input type="text" placeholder="e.g., Bangalore"
        value={formData[addressKey].city}
        onChange={e => handleAddressChange(addressKey, 'city', e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
        required={required} />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        State {required && <span className="text-red-500">*</span>}
      </label>
      <input type="text" placeholder="e.g., Karnataka"
        value={formData[addressKey].state}
        onChange={e => handleAddressChange(addressKey, 'state', e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
        required={required} />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Postal Code {required && <span className="text-red-500">*</span>}
      </label>
      <input type="text" placeholder="e.g., 560001"
        value={formData[addressKey].zipcode}
        onChange={e => handleAddressChange(addressKey, 'zipcode', e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
        required={required} />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Country {required && <span className="text-red-500">*</span>}
      </label>
      <input type="text" placeholder="e.g., India"
        value={formData[addressKey].country}
        onChange={e => handleAddressChange(addressKey, 'country', e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
        required={required} />
    </div>
  </div>
);

const CreateInvoiceModal = ({ candidates, clients, onClose, onCreate, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stepChanging, setStepChanging] = useState(false);
  const scrollRef = useRef(null);

  const [formData, setFormData] = useState({
    // Section 1: Client / Billing & Shipping
    clientId: '',
    clientName: '',
    clientEmail: '',
    poNumber: '',
    clientGSTIN: '',
    gstStatus: 'Registered',
    paymentTerms: 'Net 30 (Payment within 30 days)',
    billingAddress: {
      streetLine1: '',
      streetLine2: '',
      city: '',
      state: '',
      country: 'India',
      zipcode: ''
    },
    sameAsBilling: true,
    shippingAddress: {
      streetLine1: '',
      streetLine2: '',
      city: '',
      state: '',
      country: 'India',
      zipcode: ''
    },

    // Section 2: Placement Details
    candidateId: '',
    candidateName: '',
    designation: '',
    placementType: 'Direct Placement (Permanent)',
    ctc: '',
    joiningDate: '',

    // Section 3: Fee Structure
    feeModel: 'Percentage',
    feePercentage: '15',
    fixedAmount: '',
    hybridFixed: '',
    hybridPercentage: '10',
    monthlyAmount: '',
    duration: '3',
    hourlyRate: '',
    totalHours: '',
    calculatedFee: '',
    guaranteeDays: 90,
    guaranteeEndDate: '',
    ifCandidateLeaves: 'Replace with candidate at no extra cost',
    exitPenalty: 'Prorata Fee Reduction (50% back at day 45)',

    // Section 4: Your Company Details
    yourGSTIN: '',
    yourPan: '',
    accountNumber: '',
    ifscCode: '',
    fromAddress: {
      streetLine1: '',
      streetLine2: '',
      city: '',
      state: '',
      country: 'India',
      zipcode: ''
    },

    // Section 5: Compliance & Taxes
    isTDSApplicable: true,
    tdsRate: 'yes',
    customTDSRate: '',
    automaticPaymentReminders: true,
    sendInvoiceMailTo: '',
    sacCode: '',
    placeOfSupply: '',

    // Document checklist
    documents: {
      offerLetter: true,
      joiningLetter: true,
      form15: false,
      purchaseOrder: false
    }
  });

  const paymentTermsOptions = [
    'Net 7 (Payment within 7 days)',
    'Net 30 (Payment within 30 days)',
    'Net 45 (Payment within 45 days)',
    'Net 60 (Payment within 60 days)',
    'Immediate (Payment on invoice date)',
    'Paid When Paid (After you receive)'
  ];

  const placementTypeOptions = [
    'Direct Placement (Permanent)',
    'Contract (3-6 months)',
    'Contract-to-Hire',
    'Executive Search',
    'Temporary'
  ];

  const gstStatusOptions = [
    { value: 'Registered',   label: 'Registered (Can claim ITC)' },
    { value: 'Unregistered', label: 'Unregistered (No GST)' },
    { value: 'Individual',   label: 'Individual (Non-GST)' }
  ];

  const feeModelOptions = [
    { value: 'Percentage', label: 'Placement Fee (% of CTC)',  description: 'Contingency model • Pay after candidate joins' },
    { value: 'Fixed',      label: 'Fixed Amount',               description: 'Flat fee • Regardless of CTC' },
    { value: 'Hybrid',     label: 'Hybrid (Fixed + %)',         description: 'Risk-sharing • Fixed + percentage' },
    { value: 'Retainer',   label: 'Monthly Retainer',           description: 'Recurring • Monthly fixed fee' },
    { value: 'Hourly',     label: 'Hourly Billing',             description: 'Consulting • Hourly rate' }
  ];

  const replacementTermsOptions = [
    'Replace with candidate at no extra cost',
    'Refund proportional amount (prorata)',
    'Issue credit memo for next placement',
    'No refund - Fee is non-refundable'
  ];

  const exitPenaltyOptions = [
    'Prorata Fee Reduction (50% back at day 45)',
    'No Penalty - Full Fee Retained',
    'Flat 20% Penalty if Leaves Early'
  ];

  const tdsStatusOptions = [
    { value: 'yes',    label: 'Yes - 10% TDS on professional services (standard)' },
    { value: 'no',     label: 'No - Client claims exemption (Form 15G/15H)' },
    { value: 'custom', label: 'Custom Rate' }
  ];

  // ─────────────────────────────────────────────────────────────────────────
  // FIX 1: Client details — map billingAddress → billingAddress (client's)
  //         Address fields are: streetLine1, streetLine2, city, state,
  //         country, zipcode  (confirmed from Address.java)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchClientDetails = async () => {
      if (!formData.clientId) return;
      try {
        const response = await clientsAPI.getClientById(formData.clientId);
        const c = response.data;
        console.log('Fetched client:', c);

        // Helper: safely map an Address object using confirmed field names
        const mapAddress = (addr) => {
          if (!addr) return null;
          return {
            streetLine1: addr.streetLine1 || '',
            streetLine2: addr.streetLine2 || '',
            city:        addr.city        || '',
            state:       addr.state       || '',
            country:     addr.country     || 'India',
            zipcode:     addr.zipcode     || ''
          };
        };

        const updates = {
          clientName:   c.companyName   || c.clientName  || '',
          // FIX: prefer pocEmail for invoice contact, fall back to primary email
          clientEmail:  c.pocEmail      || c.email       || '',
          // FIX: taxIdNumber = GSTIN from Client model
          clientGSTIN:  c.taxIdNumber   || '',
          // FIX: taxStatus maps to gstStatus
          gstStatus:    c.taxStatus     || 'Registered',
          paymentTerms: c.paymentTerms  || 'Net 30 (Payment within 30 days)',
          placeOfSupply: c.billingAddress?.state ? c.billingAddress.state : '',
        };

        // FIX: map client's billingAddress → form billingAddress
        const mappedBilling = mapAddress(c.billingAddress);
        if (mappedBilling) {
          updates.billingAddress = mappedBilling;
        }

        // FIX: map client's shippingAddress → form shippingAddress
        const mappedShipping = mapAddress(c.shippingAddress);
        if (mappedShipping) {
          updates.shippingAddress = mappedShipping;
          // Only disable sameAsBilling if client explicitly set it to false
          if (c.sameAsBilling === false) {
            updates.sameAsBilling = false;
          }
        }

        setFormData(prev => ({ ...prev, ...updates }));
      } catch (error) {
        console.error('Error fetching client details:', error);
        toast.error('Failed to fetch client details');
      }
    };

    fetchClientDetails();
  }, [formData.clientId]);

  // ─────────────────────────────────────────────────────────────────────────
  // FIX 2: Candidate details — map givenCTC → ctc, actualjoiningdate → joiningDate
  //         (field names confirmed from CandidateApplication.java)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!formData.candidateId) return;
    const sel = candidates.find(c => c.id === formData.candidateId);
    if (!sel) return;

    const resolveDesignation = async () => {
      let designation = sel.currentJobTitle || '';
      const jobId = sel.jobIds?.[0];
      if (jobId) {
        try {
          const jobRes = await jobsAPI.getJobById(jobId);
          designation = jobRes.data?.jobTitle || designation;
        } catch {
          // fall back to currentJobTitle already set
        }
      }
      setFormData(prev => ({
        ...prev,
        candidateName: `${sel.firstName} ${sel.lastName}`.trim(),
        designation,
        ctc:        sel.givenCTC          || sel.currentCTC || '',
        joiningDate: sel.actualjoiningdate || ''
      }));
    };

    resolveDesignation();
  }, [formData.candidateId, candidates]);

  useEffect(() => {
    if (!formData.placementType) return;
    const type = formData.placementType.toLowerCase();
    if (type.includes('permanent') || type.includes('direct')) {
      setFormData(prev => ({ ...prev, sacCode: '998513' }));
    } else if (type.includes('contract') || type.includes('temp')) {
      setFormData(prev => ({ ...prev, sacCode: '998514' }));
    } else {
      setFormData(prev => ({ ...prev, sacCode: '998513' }));
    }
  }, [formData.placementType]);

  useEffect(() => {
    const userProfileStr = localStorage.getItem('userProfile');
    if (!userProfileStr) return;
    try {
      const profile = JSON.parse(userProfileStr);
      setFormData(prev => ({
        ...prev,
        yourGSTIN: prev.yourGSTIN || (profile.gstnumber || profile.GSTnumber || '').replace(/^GSTIN\s*/i, '').trim() || '',
        yourPan: prev.yourPan || profile.pannumber || '',
        accountNumber: prev.accountNumber || profile.accountno || '',
        ifscCode: prev.ifscCode || profile.ifsccode || '',
        fromAddress: (prev.fromAddress?.streetLine1) ? prev.fromAddress : {
          streetLine1: profile.streetLine1 || '',
          streetLine2: profile.streetLine2 || '',
          city: profile.city || '',
          state: profile.state || '',
          country: profile.country || 'India',
          zipcode: profile.zipcode || ''
        }
      }));
    } catch (e) {
      console.error('Failed to load user profile for invoice form', e);
    }
  }, []);

  // Calculate fee
  useEffect(() => {
    let calculatedFee = 0;
    const ctc = parseFloat(formData.ctc) || 0;
    switch (formData.feeModel) {
      case 'Percentage':
        if (ctc && formData.feePercentage)
          calculatedFee = (ctc * parseFloat(formData.feePercentage)) / 100;
        break;
      case 'Fixed':
        calculatedFee = parseFloat(formData.fixedAmount) || 0;
        break;
      case 'Hybrid':
        if (ctc && formData.hybridPercentage && formData.hybridFixed)
          calculatedFee = parseFloat(formData.hybridFixed) + (ctc * parseFloat(formData.hybridPercentage)) / 100;
        break;
      case 'Retainer':
        if (formData.monthlyAmount && formData.duration)
          calculatedFee = parseFloat(formData.monthlyAmount) * parseInt(formData.duration);
        break;
      case 'Hourly':
        if (formData.hourlyRate && formData.totalHours)
          calculatedFee = parseFloat(formData.hourlyRate) * parseFloat(formData.totalHours);
        break;
      default: break;
    }
    setFormData(prev => ({ ...prev, calculatedFee: calculatedFee.toFixed(2) }));
  }, [formData.feeModel, formData.ctc, formData.feePercentage, formData.fixedAmount,
      formData.hybridFixed, formData.hybridPercentage, formData.monthlyAmount,
      formData.duration, formData.hourlyRate, formData.totalHours]);

  // Calculate guarantee end date
  useEffect(() => {
    if (formData.joiningDate && formData.guaranteeDays) {
      const d = new Date(formData.joiningDate);
      d.setDate(d.getDate() + parseInt(formData.guaranteeDays));
      setFormData(prev => ({ ...prev, guaranteeEndDate: d.toISOString().split('T')[0] }));
    }
  }, [formData.joiningDate, formData.guaranteeDays]);

  // Copy billing → shipping when sameAsBilling
  useEffect(() => {
    if (formData.sameAsBilling) {
      setFormData(prev => ({ ...prev, shippingAddress: { ...prev.billingAddress } }));
    }
  }, [formData.sameAsBilling, formData.billingAddress]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAddressChange = (addressType, field, value) => {
    setFormData(prev => ({ ...prev, [addressType]: { ...prev[addressType], [field]: value } }));
  };

  const handleDocumentCheckbox = (docName, checked) => {
    setFormData(prev => ({ ...prev, documents: { ...prev.documents, [docName]: checked } }));
  };

  const handleFileChange = (e) => {
    setFiles(prev => [...prev, ...Array.from(e.target.files)]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const buildDocumentsArray = (documentsObj) => {
    if (!documentsObj) return [];
    return Object.entries(documentsObj)
      .filter(([_, value]) => value === true)
      .map(([key]) => ({ documentType: key, status: 'REQUIRED' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentStep !== 4) return;
    setLoading(true);
    try {
      const userProfileStr = localStorage.getItem('userProfile');
      let organizationId = null;
      if (userProfileStr) {
        const _p = JSON.parse(userProfileStr);
        organizationId = _p.organizationId || _p.id || localStorage.getItem('organizationId');
      }

      const formDataToSend = new FormData();
      const tdsRateStr = formData.tdsRate;
      let numericTdsRate = 0;
      if (tdsRateStr === 'no') {
        numericTdsRate = 0;
      } else if (tdsRateStr === 'yes') {
        numericTdsRate = 10;
      } else if (tdsRateStr === 'custom') {
        numericTdsRate = parseFloat(formData.customTDSRate) || 0;
      } else if (String(tdsRateStr).includes('10')) {
        numericTdsRate = 10;
      } else if (String(tdsRateStr).includes('2')) {
        numericTdsRate = 2;
      }
      const calculatedFeeNum = parseFloat(formData.calculatedFee) || 0;
      const tdsAmount = numericTdsRate > 0 ? (calculatedFeeNum * numericTdsRate / 100) : 0;
      const payload = {
        ...formData,
        documents: buildDocumentsArray(formData.documents),
        invoiceDate: new Date().toISOString().split('T')[0],
        isTDSApplicable: numericTdsRate > 0,
        tdsRate: numericTdsRate,
        tdsAmount: tdsAmount,
        netPayable: calculatedFeeNum - tdsAmount,
      };
      formDataToSend.append('placementOrderData', JSON.stringify(payload));
      formDataToSend.append('organizationId', organizationId);
      files.forEach(file => formDataToSend.append('files', file));
      formDataToSend.append('documentTypes', files.map(() => 'invoice').join(','));

      await invoiceAPI.createInvoice(formDataToSend);
      toast.success('Invoice created successfully');
      if (onSuccess) onSuccess();
      onCreate();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(error.response?.data?.message || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (currentStep === 1 && formData.yourGSTIN.trim().length !== 15) {
      toast.error('Your GSTIN must be exactly 15 characters');
      return;
    }
    setStepChanging(true);
    setCurrentStep(prev => prev + 1);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setTimeout(() => setStepChanging(false), 400);
  };

  const steps = [
    { number: 1, title: 'Client Details',        icon: <Building2 size={20} /> },
    { number: 2, title: 'Placement Details',      icon: <User size={20} /> },
    { number: 3, title: 'Fee Structure',          icon: <DollarSign size={20} /> },
    { number: 4, title: 'Compliance & Documents', icon: <Shield size={20} /> }
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="bg-white shadow-2xl w-full max-w-3xl h-full flex flex-col animate-slideInRight">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Create Invoice</h2>
            <p className="text-xs text-gray-500 mt-0.5">Step {currentStep} of 4: {steps[currentStep-1].title}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>


        {/* Progress Stepper */}
        <div className="px-8 py-4 bg-white border-b overflow-x-auto flex-shrink-0">
          <div className="flex items-center justify-between min-w-[500px]">
            {steps.map((step, idx) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center group relative cursor-pointer" onClick={() => idx + 1 < currentStep && setCurrentStep(idx + 1)}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    currentStep === step.number 
                      ? 'bg-[#1C4E6D] text-white shadow-lg ring-4 ring-[#1C4E6D]/10' 
                      : currentStep > step.number 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-100 text-gray-400 border-2 border-gray-200'
                  }`}>
                    {currentStep > step.number ? '✓' : step.icon}
                  </div>
                  <span className={`text-[10px] font-bold mt-2 uppercase tracking-wider ${
                    currentStep === step.number ? 'text-[#1C4E6D]' : 'text-gray-400'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 rounded-full transition-colors duration-500 ${
                    currentStep > step.number ? 'bg-green-500' : 'bg-gray-100'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-gray-50/50">
          <form onSubmit={handleSubmit} id="invoice-form" className="max-w-2xl mx-auto space-y-6 pb-10">


            {/* ── Step 1: Client Details ── */}
            {currentStep === 1 && (
              <section id="section-client-details" className="animate-fadeIn">
              <div className="flex items-center gap-2 mb-6 pb-2 border-b">
                <Building2 size={20} className="text-[#1C4E6D]" />
                <h3 className="text-lg font-bold text-gray-900">Client Details</h3>
              </div>
              
              <div className="space-y-6">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <p className="text-sm text-blue-800">
                    💡 <strong>Pro Tip:</strong> Select a client to auto-fill GSTIN, email, and addresses.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {/* Client selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Client <span className="text-red-500">*</span>
                    </label>
                    <select name="clientId" value={formData.clientId} onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required>
                      <option value="">-- Select Client --</option>
                      {clients?.length > 0
                        ? clients.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.companyName || c.clientName || c.name || 'Unnamed Client'}
                            </option>
                          ))
                        : <option value="" disabled>No clients available</option>
                      }
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client Email <span className="text-red-500">*</span>
                      </label>
                      <input type="email" name="clientEmail" value={formData.clientEmail}
                        onChange={handleInputChange} placeholder="finance@company.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Client PO Number (Optional)</label>
                      <input type="text" name="poNumber" value={formData.poNumber}
                        onChange={handleInputChange} placeholder="e.g., PO-2025-001234"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client GSTIN <span className="text-red-500">*</span>
                      </label>
                      <input type="text" name="clientGSTIN" value={formData.clientGSTIN}
                        onChange={handleInputChange} placeholder="29AAACR5055K1Z0" maxLength="15"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        GST Status <span className="text-red-500">*</span>
                      </label>
                      <select name="gstStatus" value={formData.gstStatus} onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required>
                        {gstStatusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Your GSTIN <span className="text-red-500">*</span>
                      </label>
                      <input type="text" name="yourGSTIN" value={formData.yourGSTIN}
                        onChange={handleInputChange} placeholder="29AAACR5055K1Z0" maxLength="15"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Terms <span className="text-red-500">*</span>
                    </label>
                    <select name="paymentTerms" value={formData.paymentTerms} onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required>
                      {paymentTermsOptions.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Place of Supply <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="placeOfSupply"
                    value={formData.placeOfSupply}
                    onChange={handleInputChange}
                    placeholder="e.g., Telangana (36)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
                    required
                  />
                </div>

                {/* Your (sender) Billing Address */}
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-md font-semibold text-gray-900 mb-4">📮 Your Billing Address</h3>
                  <AddressGrid addressKey="fromAddress" required={true} formData={formData} handleAddressChange={handleAddressChange} />
                </div>

                {/* Client Billing Address — auto-filled from client selection */}
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-md font-semibold text-gray-900 mb-1">🏢 Client Billing Address</h3>
                  <p className="text-xs text-gray-500 mb-4">Auto-filled when you select a client above</p>
                  <AddressGrid addressKey="billingAddress" required={true} formData={formData} handleAddressChange={handleAddressChange} />
                </div>

                {/* Client Shipping Address */}
                <div className="mt-6 pt-6 border-t">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-md font-semibold text-gray-900">📍 Client Shipping Address</h3>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" name="sameAsBilling" checked={formData.sameAsBilling}
                        onChange={handleInputChange} className="rounded border-gray-300" />
                      <span className="text-sm text-gray-700">Same as Client Billing</span>
                    </label>
                  </div>
                  {!formData.sameAsBilling && (
                    <AddressGrid addressKey="shippingAddress" required={!formData.sameAsBilling} formData={formData} handleAddressChange={handleAddressChange} />
                  )}
                </div>
              </div>
            </section>
            )}

            {/* ── Step 2: Placement Details ── */}
            {currentStep === 2 && (
              <section id="section-placement-details" className="animate-fadeIn">
              <div className="flex items-center gap-2 mb-6 pb-2 border-b">
                <User size={20} className="text-[#1C4E6D]" />
                <h3 className="text-lg font-bold text-gray-900">Placement Details</h3>
              </div>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Candidate <span className="text-red-500">*</span>
                    </label>
                    <select name="candidateId" value={formData.candidateId} onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required>
                      <option value="">Select Candidate</option>
                      {candidates.map(c => (
                        <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Designation <span className="text-red-500">*</span>
                    </label>
                    <input type="text" name="designation" value={formData.designation}
                      onChange={handleInputChange} placeholder="e.g., Senior Developer"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Placement Type <span className="text-red-500">*</span>
                    </label>
                    <select name="placementType" value={formData.placementType} onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required>
                      {placementTypeOptions.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CTC / Annual Salary (₹) <span className="text-red-500">*</span>
                    </label>
                    <input type="number" name="ctc" value={formData.ctc} onChange={handleInputChange}
                      placeholder="e.g., 1200000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                    <span className="text-xs text-gray-500 mt-1">
                      {formData.ctc ? '✓ Auto-filled from offered CTC' : 'Annual salary in INR'}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Joining Date <span className="text-red-500">*</span>
                    </label>
                    <input type="date" name="joiningDate" value={formData.joiningDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                    {formData.joiningDate && (
                      <span className="text-xs text-gray-500 mt-1">✓ Auto-filled from actual joining date</span>
                    )}
                  </div>
                </div>
              </div>
            </section>
            )}

            {/* ── Step 3: Fee Structure ── */}
            {currentStep === 3 && (
              <section id="section-fee-structure" className="animate-fadeIn">
              <div className="flex items-center gap-2 mb-6 pb-2 border-b">
                <DollarSign size={20} className="text-[#1C4E6D]" />
                <h3 className="text-lg font-bold text-gray-900">Fee Structure</h3>
              </div>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Your Billing Model: <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {feeModelOptions.map(option => (
                      <label key={option.value}
                        className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.feeModel === option.value
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-300 hover:border-blue-400'
                        }`}>
                        <input type="radio" name="feeModel" value={option.value}
                          checked={formData.feeModel === option.value} onChange={handleInputChange} className="mt-1" />
                        <div>
                          <div className="font-semibold text-sm text-gray-900">{option.label}</div>
                          <div className="text-xs text-gray-600 mt-1">{option.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">Fee Configuration:</h4>

                  {formData.feeModel === 'Percentage' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fee Percentage (%) <span className="text-red-500">*</span>
                        </label>
                        <input type="number" name="feePercentage" value={formData.feePercentage}
                          onChange={handleInputChange} min="5" max="50" step="0.1"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                        <span className="text-xs text-gray-500 mt-1">Standard: 15-25% of CTC</span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Calculated Fee</label>
                        <input type="text" readOnly
                          value={`₹ ${formData.calculatedFee ? parseFloat(formData.calculatedFee).toLocaleString('en-IN') : '0'}`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100" />
                        <span className="text-xs text-gray-500 mt-1">Auto-calculated from CTC</span>
                      </div>
                    </div>
                  )}

                  {formData.feeModel === 'Fixed' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fixed Amount (₹) <span className="text-red-500">*</span>
                      </label>
                      <input type="number" name="fixedAmount" value={formData.fixedAmount}
                        onChange={handleInputChange} placeholder="e.g., 150000"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                    </div>
                  )}

                  {formData.feeModel === 'Hybrid' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fixed Upfront (₹) <span className="text-red-500">*</span>
                        </label>
                        <input type="number" name="hybridFixed" value={formData.hybridFixed}
                          onChange={handleInputChange} placeholder="e.g., 50000"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fee Percentage (%) <span className="text-red-500">*</span>
                        </label>
                        <input type="number" name="hybridPercentage" value={formData.hybridPercentage}
                          onChange={handleInputChange} min="1" max="30" step="0.1"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                      </div>
                    </div>
                  )}

                  {formData.feeModel === 'Retainer' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Monthly Amount (₹) <span className="text-red-500">*</span>
                        </label>
                        <input type="number" name="monthlyAmount" value={formData.monthlyAmount}
                          onChange={handleInputChange} placeholder="e.g., 50000"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Duration (Months) <span className="text-red-500">*</span>
                        </label>
                        <input type="number" name="duration" value={formData.duration}
                          onChange={handleInputChange} min="1"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                      </div>
                    </div>
                  )}

                  {formData.feeModel === 'Hourly' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hourly Rate (₹) <span className="text-red-500">*</span>
                        </label>
                        <input type="number" name="hourlyRate" value={formData.hourlyRate}
                          onChange={handleInputChange} placeholder="e.g., 2500"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Total Hours <span className="text-red-500">*</span>
                        </label>
                        <input type="number" name="totalHours" value={formData.totalHours}
                          onChange={handleInputChange} placeholder="e.g., 40"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                      </div>
                    </div>
                  )}
                </div>

                {/* Guarantee Period */}
                <div className="mt-6 pt-6 border-t">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">🛡️ Guarantee Period & Replacement</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Guarantee Period (Days) <span className="text-red-500">*</span>
                      </label>
                      <input type="number" name="guaranteeDays" value={formData.guaranteeDays}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                      <span className="text-xs text-gray-500 mt-1">Standard: 90 days</span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Guarantee Ends On</label>
                      <input type="date" value={formData.guaranteeEndDate} readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        If Candidate Leaves: <span className="text-red-500">*</span>
                      </label>
                      <select name="ifCandidateLeaves" value={formData.ifCandidateLeaves} onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required>
                        {replacementTermsOptions.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Early Exit Penalty: <span className="text-red-500">*</span>
                      </label>
                      <select name="exitPenalty" value={formData.exitPenalty} onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required>
                        {exitPenaltyOptions.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            )}

            {/* ── Step 4: Compliance & Documents ── */}
            {currentStep === 4 && (
              <section id="section-compliance-documents" className="animate-fadeIn">
              <div className="flex items-center gap-2 mb-6 pb-2 border-b">
                <Shield size={20} className="text-[#1C4E6D]" />
                <h3 className="text-lg font-bold text-gray-900">Compliance & Documents</h3>
              </div>
              <div className="space-y-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-sm text-green-800">
                    ✓ <strong>Auto-Detected:</strong> HSN/SAC Code {formData.sacCode || '998513'} (Professional Services - Staffing/Recruitment)
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Is TDS Applicable? <span className="text-red-500">*</span>
                    </label>
                    <select name="tdsRate" value={formData.tdsRate} onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent">
                      {tdsStatusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>

                  {formData.tdsRate === 'custom' && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Custom TDS Rate (%)</label>
                      <input type="number" name="customTDSRate" value={formData.customTDSRate}
                        onChange={handleInputChange} min="0" max="100" placeholder="10"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your PAN</label>
                    <input type="text" name="yourPan" value={formData.yourPan}
                      onChange={handleInputChange} placeholder="ABCDE1234F" maxLength="10"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank Account Number <span className="text-red-500">*</span>
                    </label>
                    <input type="text" name="accountNumber" value={formData.accountNumber}
                      onChange={handleInputChange} placeholder="12345678901234"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" required />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code</label>
                    <input type="text" name="ifscCode" value={formData.ifscCode}
                      onChange={handleInputChange} placeholder="SBIN0001234" maxLength="11"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" />
                  </div>
                </div>

                {/* Documents Checklist */}
                <div className="mt-6 pt-6 border-t">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Documents Attached:</label>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                    {[
                      { key: 'offerLetter',    label: 'Offer Letter' },
                      { key: 'joiningLetter',  label: 'Joining Letter/Confirmation' },
                      { key: 'form15',         label: 'Form 15G/15H (if TDS exemption)' },
                      { key: 'purchaseOrder',  label: 'Purchase Order from Client' }
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" checked={formData.documents[key]}
                          onChange={e => handleDocumentCheckbox(key, e.target.checked)}
                          className="rounded border-gray-300" />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Send Invoice To */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Send Invoice To:</label>
                  <input type="email" name="sendInvoiceMailTo" value={formData.sendInvoiceMailTo}
                    onChange={handleInputChange} placeholder="finance@company.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent" />
                </div>

                {/* File Upload */}
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Attach Supporting Documents</h3>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <input type="file" id="file-upload" multiple onChange={handleFileChange} className="hidden" />
                    <label htmlFor="file-upload" className="flex flex-col items-center justify-center cursor-pointer">
                      <Upload size={48} className="text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">Click to upload or drag and drop</span>
                      <span className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, XLS, XLSX (max 10MB each)</span>
                    </label>
                  </div>
                  {files.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                          <div className="flex items-center gap-3">
                            <FileText size={20} className="text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{file.name}</p>
                              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                            </div>
                          </div>
                          <button type="button" onClick={() => removeFile(index)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>
            )}
          </form>

        </div>

        {/* Footer Navigation */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-between flex-shrink-0">
          <button type="button"
            onClick={() => { if (currentStep > 1) { setCurrentStep(currentStep - 1); if (scrollRef.current) scrollRef.current.scrollTop = 0; } }}
            disabled={currentStep === 1}
            className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${
              currentStep === 1 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-gray-300'
            }`}>
            Back
          </button>
          
          <div className="flex items-center gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-700">
              Cancel
            </button>
            {currentStep < 4 ? (
              <button type="button"
                onClick={handleContinue}
                className="px-8 py-2 bg-[#1C4E6D] text-white rounded-lg text-sm font-bold shadow-lg shadow-[#1C4E6D]/20 hover:bg-[#153b52] transition-all flex items-center gap-2">
                Continue <ChevronRight size={16} />
              </button>
            ) : (
              <button type="submit" form="invoice-form" disabled={loading || stepChanging}
                className="px-8 py-2 bg-[#1C4E6D] text-white rounded-lg text-sm font-bold shadow-lg shadow-[#1C4E6D]/20 hover:bg-[#153b52] transition-all disabled:opacity-50 flex items-center gap-2">
                {loading ? 'Processing...' : 'Generate Invoice'}
                {!loading && <FileText size={16} />}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default CreateInvoiceModal;